import React from 'react';
import { createPortal } from 'react-dom';
import { gbFmt } from './gbApi';
import { GbCoin } from './GbCoin';

const Sec = ({ n, title, children, testid }) => (
  <div className="mt-2.5 rounded-xl border border-purple-900/70 bg-black/50 p-3" data-testid={testid}>
    <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-amber-400">
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-black">{n}</span>
      {title}
    </p>
    <div className="space-y-1 text-[10px] leading-relaxed text-purple-200/85">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex gap-1.5">
    <span className="shrink-0 text-amber-600">▸</span><span>{children}</span>
  </p>
);

const Tabela = ({ linhas, pote, titulo }) => (
  <div className="mt-2 overflow-hidden rounded-xl border border-amber-900/60" data-testid="gb-rank-prize-split">
    <div className="flex bg-amber-950/40 px-2 py-1 text-[8.5px] font-black uppercase tracking-[0.15em] text-amber-500">
      <span className="w-10">Lugar</span>
      <span className="w-14 text-center">Fatia</span>
      <span className="flex-1 text-right">{titulo}</span>
    </div>
    {linhas.map((l) => (
      <div key={l.rank} data-testid={`gb-rank-prize-row-${l.rank}`}
        className={`flex items-center px-2 py-1 text-[10px] ${l.rank <= 3
          ? 'bg-black/60 font-black text-amber-200' : 'bg-black/30 text-purple-200'}`}>
        <span className="w-10">{l.rank}º</span>
        <span className="w-14 text-center text-amber-500">{l.fatia}</span>
        <span className="flex-1 text-right"><GbCoin /> {gbFmt(l.valor)}</span>
      </div>
    ))}
    {pote !== undefined && (
      <p className="bg-black/50 px-2 py-1 text-[8.5px] font-bold text-purple-400">
        Previsão com o acumulado de agora (<GbCoin /> {gbFmt(pote)}) — sobe a cada duelo.
      </p>
    )}
  </div>
);

export const GbRankRules = ({ kind, dados, onClose }) => {
  const titulo = { level: 'Regras do Ranking de Nível', pvp: 'Regras do Ranking de PvP',
    clan: 'Regras do Ranking de Clãs' }[kind];
  const premios = dados?.prizes || [];
  const pote = Number(dados?.jackpot || 0);
  const shares = dados?.shares || [];
  const membros = dados?.member_shares || [];

  return createPortal(
    <div className="pwa-safe-top fixed inset-0 z-[97] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="gb-rank-rules-overlay" onClick={onClose}>
      <div className="w-full max-w-md p-3" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" style={{ background: 'linear-gradient(160deg,#f2d387,#6e5118 40%,#c99b3f 70%,#f2d387)', borderRadius: '18px' }}>
          <div className="max-h-[86vh] overflow-y-auto rounded-[16px] bg-[#0b0712] p-3.5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-300">
                <img src="/gb/ic_regras.webp" alt="" className="h-7 w-7 object-contain" />
                {titulo}
              </p>
              <button onClick={onClose} data-testid="gb-rank-rules-close"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
            </div>

            {kind === 'level' && (
              <>
                <Sec n="1" title="Como pontuar" testid="gb-rules-level-how">
                  <Li>O ranking usa o <b className="text-amber-300">XP acumulado</b> na temporada — ele nunca cai.</Li>
                  <Li>Duelo de PvP dá até <b className="text-amber-300">100 XP</b> por partida (vitória rende mais).</Li>
                  <Li>As fases dão pouco XP: servem para treinar e ganhar moedas, não para subir no ranking.</Li>
                  <Li>Conta bloqueada por fraude sai do ranking e perde o prêmio.</Li>
                </Sec>
                <Sec n="2" title="Prêmio fixo do top 3" testid="gb-rules-level-prize">
                  <Li>Só os <b className="text-amber-300">3 primeiros</b> recebem, com valor fixo — não depende de jackpot.</Li>
                  <Tabela linhas={premios.map((p) => ({ rank: p.rank, fatia: 'fixo', valor: p.coins }))} titulo="Prêmio" />
                </Sec>
              </>
            )}

            {kind === 'pvp' && (
              <>
                <Sec n="1" title="Como pontuar" testid="gb-rules-pvp-how">
                  <Li>Vale o número de <b className="text-amber-300">vitórias em duelos ao vivo</b>.</Li>
                  <Li>Entrada de 1.000 moedas por jogador; o vencedor leva 1.800.</Li>
                  <Li>Vitória por desistência ou abandono do adversário também conta.</Li>
                  <Li>Empate no número de vitórias é decidido pelo XP.</Li>
                </Sec>
                <Sec n="2" title="Jackpot do PvP" testid="gb-rules-pvp-jackpot">
                  <Li>Cada duelo deixa <b className="text-amber-300">200 moedas</b> para a distribuição de prêmios do evento.</Li>
                  <Li>No fim do evento o servidor congela o top 3 e o admin libera o pagamento.</Li>
                  <Tabela pote={pote} titulo="Prêmio de hoje"
                    linhas={shares.map((s) => ({ rank: s.rank, fatia: `${s.pct}%`,
                      valor: Math.floor((pote * Number(s.pct)) / 100) }))} />
                </Sec>
              </>
            )}

            {kind === 'clan' && (
              <>
                <Sec n="1" title="Como o clã pontua" testid="gb-rules-clan-how">
                  <Li>A pontuação do clã é a <b className="text-amber-300">soma do que cada membro fez dentro dele</b>.</Li>
                  <Li>Você pontua jogando: cada XP ganho em fase ou PvP vira ponto para o clã.</Li>
                  <Li>Quem <b className="text-rose-300">sai ou é expulso perde os pontos</b> — e o clã perde também.</Li>
                  <Li>Toque na posição do clã para ver a contribuição de cada membro.</Li>
                </Sec>
                <Sec n="2" title="Prêmio dos clãs" testid="gb-rules-clan-prize">
                  <Li>Só os <b className="text-amber-300">3 primeiros clãs</b> recebem.</Li>
                  <Tabela linhas={premios.map((p) => ({ rank: p.rank, fatia: 'fixo', valor: p.coins }))} titulo="Prêmio do clã" />
                </Sec>
                <Sec n="3" title="Divisão entre os membros" testid="gb-rules-clan-split">
                  <Li>O prêmio do clã é dividido entre os <b className="text-amber-300">5 que mais pontuaram</b> nele.</Li>
                  <div className="mt-2 overflow-hidden rounded-xl border border-amber-900/60">
                    <div className="flex bg-amber-950/40 px-2 py-1 text-[8.5px] font-black uppercase tracking-[0.15em] text-amber-500">
                      <span className="w-14">No clã</span>
                      <span className="flex-1 text-right">Fatia do prêmio</span>
                    </div>
                    {membros.map((m) => (
                      <div key={m.rank} className="flex items-center bg-black/40 px-2 py-1 text-[10px] text-amber-200">
                        <span className="w-14 font-black">{m.rank}º</span>
                        <span className="flex-1 text-right text-amber-500">{m.pct}%</span>
                      </div>
                    ))}
                  </div>
                </Sec>
              </>
            )}

            <Sec n="★" title="Vale para todos os rankings" testid="gb-rules-common">
              <Li>O crédito cai na sua <b className="text-amber-300">conta KAKO</b> depois da liberação, e o bot avisa no chat.</Li>
              <Li>Conta com contestação de pagamento em aberto <b className="text-amber-300">será banida</b> e fica retida até resolver.</Li>
            </Sec>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
